import React, { useState, useEffect } from 'react';
import { BookOpen, Play, Pause, GitBranch } from 'lucide-react';
import LearnConceptsTab from './LearnConceptsTab';
import MeetAgentsTab from './MeetAgentsTab';
import WorkflowTab from './WorkflowTab';
import LiveDemoTab from './LiveDemoTab';
import VisualFlowTab from './VisualFlowTab';
import { A2AClient } from '../lib/a2a-client';

/**
 * Main Teaching Aids Component
 * 
 * Interactive learning platform for understanding A2A communication
 * Adapted from Agent2AgentTeachingAid.js reference
 */
const TeachingAidsMain = () => {
  const [activeTab, setActiveTab] = useState('learn');
  const [isRunning, setIsRunning] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [a2aClient, setA2aClient] = useState(null);
  const [capabilities, setCapabilities] = useState([]);
  const [learnerData, setLearnerData] = useState(null);

  // Initialize A2A Client on mount
  useEffect(() => {
    const initClient = async () => {
      try {
        // Use the correct backend URL
        const backendUrl = import.meta.env.VITE_BACKEND_URL || 'https://a2ateachingaids-server.appsokay.net';
        
        console.log('🔗 Connecting to backend:', backendUrl);
        
        const client = new A2AClient(
          backendUrl,
          'teaching-aids-frontend',
          { enableLogging: true }
        );

        // Discover capabilities
        const caps = await client.discoverCapabilities();
        
        setA2aClient(client);
        setCapabilities(caps);
        console.log('✅ A2A Client initialized', caps);
      } catch (error) {
        console.error('❌ Failed to initialize A2A Client:', error);
        console.error('Backend URL attempted:', import.meta.env.VITE_BACKEND_URL || 'https://a2ateachingaids-server.appsokay.net');
      }
    };

    initClient();

    // Cleanup on unmount
    return () => {
      if (a2aClient) {
        a2aClient.cleanup();
      }
    };
  }, []);

  const tabs = [
    { id: 'learn', label: 'Learn Concepts', icon: <BookOpen className="w-4 h-4" /> },
    { id: 'agents', label: 'Meet the Agents', icon: <span className="w-4 h-4">🤖</span> },
    { id: 'workflow', label: 'See Workflow', icon: <span className="w-4 h-4">🔄</span> },
    { id: 'visual', label: 'Visual Flow', icon: <GitBranch className="w-4 h-4" /> },
    { id: 'demo', label: 'Live Demo', icon: <Play className="w-4 h-4" /> }
  ];

  const startDemo = () => {
    setIsRunning(true);
    setCurrentStep(0);
    // Demo logic will be handled in LiveDemoTab
  };

  const stopDemo = () => {
    setIsRunning(false);
    setCurrentStep(0);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-gradient-to-br from-indigo-600 to-purple-600 p-2 rounded-lg">
                <BookOpen className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  A2A Teaching Aids System
                </h1>
                <p className="text-sm text-gray-500">
                  Interactive Learning Platform: Understanding Agent-to-Agent Communication
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="text-right mr-3">
                <div className="text-sm font-medium text-gray-700">Learning Mode</div>
                <div className="text-xs text-gray-500">
                  {capabilities.length} capabilities available
                </div>
              </div>
              <button
                onClick={isRunning ? stopDemo : startDemo}
                disabled={!a2aClient}
                className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all ${
                  !a2aClient
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : isRunning 
                    ? 'bg-red-600 text-white hover:bg-red-700' 
                    : 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-700 hover:to-purple-700 shadow-lg'
                }`}
              >
                {isRunning ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                <span>{isRunning ? 'Stop Demo' : 'Start Interactive Demo'}</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex space-x-8">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.icon}
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {activeTab === 'learn' && <LearnConceptsTab />}
        {activeTab === 'agents' && <MeetAgentsTab capabilities={capabilities} />}
        {activeTab === 'workflow' && <WorkflowTab />}
        {activeTab === 'visual' && (
          <VisualFlowTab
            a2aClient={a2aClient}
            isRunning={isRunning}
            setIsRunning={setIsRunning}
          />
        )}
        {activeTab === 'demo' && (
          <LiveDemoTab 
            a2aClient={a2aClient}
            isRunning={isRunning}
            setIsRunning={setIsRunning}
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
          />
        )}
      </main>
    </div>
  );
};

export default TeachingAidsMain;

